import { useCallback } from "react";
import { useLocation } from "wouter";

// Track in-app navigation depth via history.state._appDepth.
// Monkey-patching pushState is a standard pattern (used by analytics libraries, etc.)
// and is framework-independent — no reliance on wouter internals.
if (typeof window !== "undefined") {
  // Mark the initial entry if it doesn't already have a depth (preserves value across refresh)
  if (history.state?._appDepth == null) {
    history.replaceState({ ...history.state, _appDepth: 0 }, "");
  }

  const originalPushState = history.pushState;
  history.pushState = function (state, title, url) {
    const currentDepth = history.state?._appDepth ?? 0;
    const newState =
      typeof state === "object" && state !== null
        ? { ...state, _appDepth: currentDepth + 1 }
        : { _appDepth: currentDepth + 1 };
    return originalPushState.call(this, newState, title, url);
  };

  // replaceState swaps the current entry without growing the stack, so the
  // depth must stay the same. Without this patch, callers that pass a
  // fresh state object (notably wouter's `setLocation(url, { replace: true })`
  // which sets state to `null`) would wipe out `_appDepth`, making
  // `useBackNavigation` think there's no in-app history and fall back to /.
  const originalReplaceState = history.replaceState;
  history.replaceState = function (state, title, url) {
    const currentDepth = history.state?._appDepth ?? 0;
    const newState =
      typeof state === "object" && state !== null
        ? { ...state, _appDepth: currentDepth }
        : { _appDepth: currentDepth };
    return originalReplaceState.call(this, newState, title, url);
  };
}

/**
 * Returns a callback that navigates to the previous page.
 * Uses history.back() when there's in-app history (depth > 0),
 * otherwise falls back to "/" (fresh tab, external referrer, or app entry point).
 */
export function useBackNavigation() {
  const [, setLocation] = useLocation();

  return useCallback(() => {
    if ((history.state?._appDepth ?? 0) > 0) {
      history.back();
    } else {
      setLocation("/");
    }
  }, [setLocation]);
}
